import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const RPMMCalculator = () => {
  const [rpmData, setRpmData] = useState({
    rotations: '',
    time: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRpmData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateRPM = () => {
    const { rotations, time } = rpmData;

    if (!rotations || !time) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const rotationsNum = parseFloat(rotations);
      const timeNum = parseFloat(time);

      if (isNaN(rotationsNum) || isNaN(timeNum) || timeNum <= 0) {
        setError('Please enter valid numbers for rotations and time');
        return;
      }

      const rpm = (rotationsNum / timeNum) * 60;
      const resultsData = {
        rotations,
        time,
        rpm: rpm.toFixed(2)
      };

      setResults(resultsData);
      setChartData(generateChartData(resultsData));
    } catch (error) {
      setError('Please enter valid numbers for all fields');
    }
  };

  const generateChartData = (results) => {
    const labels = Array.from({ length: parseInt(results.time) }, (_, i) => i + 1);
    const theoreticalData = labels.map(() => results.rpm);

    return {
      labels,
      datasets: [
        {
          label: 'Theoretical RPM',
          data: theoreticalData,
          borderColor: 'rgb(75, 192, 192)',
          tension: 0.1,
          fill: false
        }
      ]
    };
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>RPM Calculator | Rotations per Minute Tool</title>
        <meta name="description" content="Calculate rotations per minute (RPM) with our easy-to-use calculator. Input rotations and time to get RPM values and visualize data." />
        <meta name="keywords" content="RPM calculator, rotations per minute, mechanical engineering tools, RPM tool" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-blue-600 mr-4">⚙️</div>
            <h1 className="text-3xl font-bold text-gray-800">RPM Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>The RPM Calculator helps you determine rotations per minute based on the number of rotations and the time taken.</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Rotations:</label>
                <input
                  type="text"
                  id="rotations"
                  name="rotations"
                  value={rpmData.rotations}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter number of rotations"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time (seconds):</label>
                <input
                  type="text"
                  id="time"
                  name="time"
                  value={rpmData.time}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter time in seconds"
                />
              </div>
            </div>

            <button
              onClick={calculateRPM}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
            >
              Calculate RPM
            </button>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            {results && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">RPM Analysis</h2>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Rotations per Minute</h3>
                  <p className="text-gray-600">{results.rpm} RPM</p>
                </div>

                {chartData && (
                  <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                    <Line
                      data={chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'RPM'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Time (seconds)'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'RPM over Time',
                            font: {
                              size: 16
                            }
                          }
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </motion.div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding RPM <span className="text-blue-600">⚙️</span></h2>
            <div className="text-gray-600 mb-8">
              <p>RPM (Rotations per Minute) is a measure of the frequency of a rotation. It is commonly used in mechanical engineering to describe the speed of rotating objects.</p>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use RPM Calculator <span className="text-blue-600">📋</span></h2>
            <div className="text-gray-600">
              <p>Follow these steps to use the RPM Calculator effectively:</p>
              
              <div className="mt-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Prepare Your Data</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Count the number of rotations</li>
                  <li>Measure the time taken for the rotations</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Enter Data</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the number of rotations in the first field</li>
                  <li>Enter the time taken in the second field</li>
                </ul>
              </div>

              <div className="mt-6">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Analyze Results</h3>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the calculated RPM value</li>
                  <li>Visualize the RPM over time</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">🔍</span></h2>
            <div className="text-gray-600">
              <p>Suppose you have a motor that completes 300 rotations in 5 seconds. To calculate the RPM:</p>
              <ul className="list-disc pl-6 mt-2">
                <li>Enter <strong>300</strong> in the "Number of Rotations" field</li>
                <li>Enter <strong>5</strong> in the "Time (seconds)" field</li>
                <li>Click "Calculate RPM"</li>
                <li>The calculated RPM will be <strong>3600 RPM</strong></li>
              </ul>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ <span className="text-blue-600">❓</span></h2>
            <div className="text-gray-600">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">What is RPM?</h3>
                <p>RPM stands for Rotations per Minute. It is a unit of rotational speed that indicates how many complete rotations an object makes in one minute.</p>
              </div>

              <div className="mb-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">Why is RPM important?</h3>
                <p>RPM is crucial in various fields, including automotive, manufacturing, and engineering, as it helps in understanding the speed of rotating machinery, optimizing performance, and ensuring safety.</p>
              </div>

              <div className="mb-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">How do I measure rotations and time?</h3>
                <p>You can use a tachometer to measure the speed of a rotating object directly in RPM. Alternatively, you can manually count the number of rotations over a specific time period using a stopwatch.</p>
              </div>

              <div className="mb-4">
                <h3 className="text-lg font-semibold text-blue-600 mb-2">What if my time is in minutes?</h3>
                <p>If your time is in minutes, simply convert it to seconds by multiplying by 60 before entering it into the calculator.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RPMMCalculator;